import { useCallback, useEffect, useMemo, useState } from 'react'
import { buildTestParamsPayload, getApplicantModelParamKeys, getApplicationModelParamKeys, hasAnyModelParamSelected } from '../utils/modelCalculationParams'
import {
  applyBulkValuesToForm,
  buildInitialTestFormValues,
  clearTestFormValues,
  getApplicantCountFromForm,
  isTestFormValid,
  syncApplicantRows,
  type ModelTestFormValues,
} from '../utils/modelTestPklForm'
import { validateModelPkl } from '../services/modelService'
import type {
  ModelCalculationParamSelection,
  ModelParamDefinition,
  PklValidationResult,
  PklValidationTest,
} from '../types/modelParam'
import { showSuccess } from '@/lib/utils/notifications'

export interface ModelTestPklDialogProps {
  modelId: number
  modelName: string
  modelProcessorName?: string | null
  pklName?: string | null
  paramSelection: ModelCalculationParamSelection
  paramDefinitions: ModelParamDefinition[]
}

function createEmptyFormState(): {
  formValues: ModelTestFormValues
  bulkValues: string
  bulkValuesError: string
  submitted: boolean
  testResult: PklValidationResult | null
  testError: PklValidationResult | string | null
  jsonCopied: boolean
} {
  return {
    formValues: {},
    bulkValues: '',
    bulkValuesError: '',
    submitted: false,
    testResult: null,
    testError: null,
    jsonCopied: false,
  }
}

export function useModelTestPkl({
  isOpen,
  modelId,
  paramSelection,
  paramDefinitions,
}: Pick<ModelTestPklDialogProps, 'modelId' | 'paramSelection' | 'paramDefinitions'> & {
  isOpen: boolean
}) {
  const applicationParamKeys = useMemo(
    () => getApplicationModelParamKeys(paramDefinitions),
    [paramDefinitions],
  )
  const applicantParamKeys = useMemo(
    () => getApplicantModelParamKeys(paramDefinitions),
    [paramDefinitions],
  )

  const hasSelection = hasAnyModelParamSelected(paramSelection, paramDefinitions)
  const hasApplicantFields = applicantParamKeys.some((field) => paramSelection[field])

  const buildInitialForm = useCallback(
    () =>
      buildInitialTestFormValues(
        applicationParamKeys,
        applicantParamKeys,
        paramSelection,
        paramDefinitions,
      ),
    [applicationParamKeys, applicantParamKeys, paramDefinitions, paramSelection],
  )

  const [formValues, setFormValues] = useState<ModelTestFormValues>(() => buildInitialForm())
  const [bulkValues, setBulkValues] = useState('')
  const [bulkValuesError, setBulkValuesError] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [testResult, setTestResult] = useState<PklValidationResult | null>(null)
  const [testError, setTestError] = useState<PklValidationResult | string | null>(null)
  const [jsonCopied, setJsonCopied] = useState(false)

  const resetDialog = useCallback(() => {
    const empty = createEmptyFormState()
    setFormValues(buildInitialForm())
    setBulkValues(empty.bulkValues)
    setBulkValuesError(empty.bulkValuesError)
    setSubmitted(empty.submitted)
    setTestResult(empty.testResult)
    setTestError(empty.testError)
    setJsonCopied(empty.jsonCopied)
    setProcessing(false)
  }, [buildInitialForm])

  useEffect(() => {
    if (!isOpen) return
    resetDialog()
  }, [isOpen, modelId, paramSelection, resetDialog])

  const applicantCount = getApplicantCountFromForm(paramSelection, formValues)

  useEffect(() => {
    if (!isOpen || !hasApplicantFields) return
    setFormValues((prev) => syncApplicantRows(prev, applicantParamKeys, paramSelection, paramDefinitions))
  }, [applicantCount, applicantParamKeys, hasApplicantFields, isOpen, paramDefinitions, paramSelection])

  const insertBulkValues = () => {
    const { next, error } = applyBulkValuesToForm(
      bulkValues,
      formValues,
      applicationParamKeys,
      applicantParamKeys,
      paramSelection,
      paramDefinitions,
    )
    if (error) {
      setBulkValuesError(error)
      return
    }
    setFormValues(next)
    setBulkValuesError('')
  }

  const clearAllFieldValues = () => {
    setFormValues(
      clearTestFormValues(
        applicationParamKeys,
        applicantParamKeys,
        paramSelection,
        paramDefinitions,
        formValues,
      ),
    )
    setBulkValues('')
    setBulkValuesError('')
    setSubmitted(false)
  }

  const updateApplicationField = (field: string, value: string | number | boolean) => {
    setFormValues((prev) => ({ ...prev, [field]: value }))
  }

  const updateApplicantField = (index: number, field: string, value: string | number | boolean) => {
    setFormValues((prev) => {
      const applicants = Array.isArray(prev.applicants) ? [...prev.applicants] : []
      applicants[index] = { ...applicants[index], [field]: value }
      return { ...prev, applicants }
    })
  }

  const formIsValid = isTestFormValid(
    formValues,
    applicationParamKeys,
    applicantParamKeys,
    paramSelection,
  )

  const submitForm = async () => {
    setSubmitted(true)
    setTestResult(null)
    setTestError(null)

    if (!formIsValid) return

    setProcessing(true)
    try {
      const params = buildTestParamsPayload(paramSelection, formValues, paramDefinitions)
      const result = await validateModelPkl(modelId, params)
      setTestResult(result)
      showSuccess('Model file test completed successfully.')
    } catch (error) {
      const err = error as Error & { data?: PklValidationResult }
      setTestError(err.data ?? err.message ?? 'Failed to test model files.')
    } finally {
      setProcessing(false)
    }
  }

  const continueToForm = () => {
    setTestResult(null)
    setTestError(null)
    setSubmitted(false)
    setJsonCopied(false)
  }

  const copyExecutionResult = async (json: string) => {
    if (!json) return
    try {
      await navigator.clipboard.writeText(json)
      setJsonCopied(true)
      window.setTimeout(() => setJsonCopied(false), 2000)
    } catch {
      // clipboard unavailable
    }
  }

  const validationTests: PklValidationTest[] =
    testResult?.tests ?? (typeof testError === 'object' && testError?.tests ? testError.tests : [])

  const showResults = !!(testResult || (submitted && testError)) && !processing
  const totalChecks = validationTests.length
  const passedCount = validationTests.filter((test) => test.success).length
  const failedCount = validationTests.filter((test) => !test.success).length
  const allPassed = !!testResult && failedCount === 0
  const successRate = totalChecks ? Math.round((passedCount / totalChecks) * 100) : 0

  const executionResult =
    testResult?.execution_response?.diagnostics != null ? testResult.execution_response.diagnostics : null
  const executionResultJson = executionResult ? JSON.stringify(executionResult, null, 2) : ''

  const resultMessage =
    typeof testError === 'object' && testError?.message
      ? testError.message
      : allPassed
        ? 'All validations passed successfully.'
        : 'One or more validations failed.'

  const headerSubtitle = showResults
    ? 'Validation summary for uploaded model file'
    : 'Provide test values for the selected calculation variables'

  return {
    hasSelection,
    applicationParamKeys,
    applicantParamKeys,
    hasApplicantFields,
    applicantCount,
    formValues,
    bulkValues,
    bulkValuesError,
    submitted,
    processing,
    showResults,
    validationTests,
    allPassed,
    totalChecks,
    passedCount,
    failedCount,
    successRate,
    executionResult,
    executionResultJson,
    resultMessage,
    headerSubtitle,
    jsonCopied,
    testError,
    setBulkValues,
    insertBulkValues,
    clearAllFieldValues,
    updateApplicationField,
    updateApplicantField,
    submitForm,
    continueToForm,
    copyExecutionResult,
    isFormValid: formIsValid,
  }
}

export function getMissingParams(test: PklValidationTest): string[] {
  const missing = test.details?.validation_result?.missing_params
  return Array.isArray(missing) ? missing.filter(Boolean) : []
}

export function getRequiredParams(test: PklValidationTest): string[] {
  const required = test.details?.validation_result?.required_params
  return Array.isArray(required) ? required.filter(Boolean) : []
}

export function getValidationDetailMessage(test: PklValidationTest): string {
  return test.details?.validation_result?.message ?? ''
}
